import React from 'react';
import { usePageContent } from '../hooks/usePageContent';
import { usePageTitle } from '../hooks/usePageTitle';

export default function About() {
    const { content, loading, error } = usePageContent('about');

    usePageTitle('About');

    if (loading) {
        return <div className="container-page pt-20">Loading...</div>;
    }

    if (error || !content) {
        return (
            <div className="container-page pt-20">
                <p className="text-red-600">Error loading content: {error}</p>
                <p className="mt-4 text-gray-600">Please contact the admin to set up the About page content.</p>
            </div>
        );
    }

    const {
        eyebrow = 'About',
        h1Tagline = '',
        h1Paragraph = '',
        bioParagraphs = [],
        values = [],
        valuesEyebrow = 'How I work',
        valuesHeading = "A few things I've come to believe.",
        timeline = [],
        timelineEyebrow = 'Experience',
        timelineHeading = "Where I've been.",
        education = [],
        educationEyebrow = 'Education',
        educationHeading = "A multi-continent classroom.",
        certs = [],
        certsEyebrow = 'Certifications'
    } = content;

    return (
        <>
            {/* Intro */}
            <section className="container-page pt-12 md:pt-28 pb-8">
                <p className="eyebrow mb-6">{eyebrow}</p>
                <h1 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tightest text-fg max-w-[20ch]">
                    {h1Tagline}
                </h1>
            </section>

            {/* Bio + portrait */}
            <section className="container-page pb-16">
                <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
                    <div className="col-span-12 text-[17px] text-fg-muted leading-[1.75] space-y-5 max-w-prose">
                        <p>{h1Paragraph}</p>
                        {bioParagraphs && bioParagraphs.map((para, idx) => (
                            <p key={idx}>{para}</p>
                        ))}
                    </div>
                </div>
            </section>

            {/* Principles */}
            {values && values.length > 0 && (
                <section className="border-t border-line bg-elevated">
                    <div className="container-page py-12 md:py-24 grid grid-cols-12 gap-8">
                        <div className="col-span-12 md:col-span-4">
                            <p className="eyebrow mb-3">{valuesEyebrow}</p>
                            <h2 className="font-display text-3xl md:text-4xl tracking-tight max-w-[16ch]">
                                {valuesHeading}
                            </h2>
                        </div>
                        <ul className="col-span-12 md:col-span-8 divide-y divide-line border-y border-line">
                            {values.map((v, i) => (
                                <li key={i} className="py-5 flex gap-6 items-baseline">
                                    <span className="font-mono text-[11px] text-fg-soft w-8 shrink-0">
                                        0{i + 1}
                                    </span>
                                    <p className="font-display text-[18px] md:text-[22px] tracking-tight leading-snug">
                                        {v}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}

            {/* Experience */}
            {timeline && timeline.length > 0 && (
                <section className="border-t border-line">
                    <div className="container-page py-12 md:py-24">
                        <div className="grid grid-cols-12 gap-8">
                            <div className="col-span-12 md:col-span-4">
                                <p className="eyebrow mb-3">{timelineEyebrow}</p>
                                <h2 className="font-display text-3xl md:text-4xl tracking-tight max-w-[14ch]">
                                    {timelineHeading}
                                </h2>
                            </div>
                            <ol className="col-span-12 md:col-span-8 space-y-10">
                                {timeline.map((t, idx) => (
                                    <li key={idx} className="grid grid-cols-12 gap-4 border-b border-line pb-10 last:border-0">
                                        <div className="col-span-12 md:col-span-4">
                                            <p className="font-mono text-[12px] text-fg-soft">{t.period}</p>
                                        </div>
                                        <div className="col-span-12 md:col-span-8">
                                            <h3 className="font-display text-[20px] tracking-tight">{t.role}</h3>
                                            <p className="text-[14px] text-fg-muted mt-1">{t.org}</p>
                                            <p className="text-[15px] text-fg-muted mt-3 leading-relaxed">{t.body}</p>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>
                </section>
            )}

            {/* Education + Certs */}
            {education && education.length > 0 && (
                <section className="border-t border-line bg-elevated">
                    <div className="container-page py-12 md:py-24">
                        <div className="grid grid-cols-12 gap-8">
                            <div className="col-span-12 md:col-span-4">
                                <p className="eyebrow mb-3">{educationEyebrow}</p>
                                <h2 className="font-display text-3xl md:text-4xl tracking-tight max-w-[14ch]">
                                    {educationHeading}
                                </h2>
                            </div>
                            <ol className="col-span-12 md:col-span-8 space-y-8">
                                {education.map((e, idx) => (
                                    <li key={idx} className="grid grid-cols-12 gap-4 border-b border-line pb-8 last:border-0">
                                        <div className="col-span-12 md:col-span-4">
                                            <p className="font-mono text-[12px] text-fg-soft">{e.period}</p>
                                        </div>
                                        <div className="col-span-12 md:col-span-8">
                                            <h3 className="font-display text-[18px] tracking-tight">{e.program}</h3>
                                            <p className="text-[14px] text-fg-muted mt-1">{e.school}</p>
                                            <p className="text-[13px] text-fg-soft mt-2">{e.note}</p>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>

                        {certs && certs.length > 0 && (
                            <div className="grid grid-cols-12 gap-8 mt-16">
                                <div className="col-span-12 md:col-span-4">
                                    <p className="eyebrow mb-3">{certsEyebrow}</p>
                                </div>
                                <ul className="col-span-12 md:col-span-8 flex flex-wrap gap-2">
                                    {certs.map((c, idx) => {
                                        // Support both legacy plain-string certs and the
                                        // newer { name, url, badge } object shape.
                                        const cert = typeof c === 'string' ? { name: c } : (c || {});
                                        if (!cert.name) return null;

                                        const inner = (
                                            <>
                                                {cert.badge && (
                                                    <img
                                                        src={cert.badge}
                                                        alt=""
                                                        className="w-4 h-4 rounded-sm object-contain shrink-0"
                                                        loading="lazy"
                                                    />
                                                )}
                                                <span>{cert.name}</span>
                                                {cert.url && (
                                                    <span className="text-fg-soft" aria-hidden="true">↗</span>
                                                )}
                                            </>
                                        );

                                        return (
                                            <li key={idx}>
                                                {cert.url ? (
                                                    <a
                                                        href={cert.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title="View verified credential"
                                                        className="pill hover:text-fg hover:border-fg transition-colors"
                                                    >
                                                        {inner}
                                                    </a>
                                                ) : (
                                                    <span className="pill">{inner}</span>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                    </div>
                </section>
            )}
        </>
    );
}
